import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getClients, createClient, getClient, updateClient, deleteClient, getClientsHealthSummary, getClientHealth, getClientMembers, inviteClientMember, removeClientMember } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import {
  Plus,
  Buildings,
  Scales,
  GearSix,
  Sliders,
  Pencil,
  Trash,
  Check,
  X as XIcon,
  CaretRight,
  Brain,
  Funnel
} from '@phosphor-icons/react';

// Empty profile template
const emptyProfile = {
  name: '',
  company_identity: {
    company_name: '',
    industry: '',
    business_model: '',
    revenue_streams: [],
    entity_structure: '',
    locations: [],
    departments: []
  },
  accounting_policy: {
    accounting_basis: 'accrual',
    capitalization_threshold: null,
    prepaid_expense_policy: '',
    revenue_recognition_notes: '',
    reimbursement_treatment: '',
    loan_handling_rules: '',
    owner_compensation_logic: ''
  },
  operational_context: {
    seasonality: '',
    top_vendors: [],
    common_payment_rails: [],
    monthly_close_process: '',
    large_known_contracts: [],
    expected_expense_cycles: ''
  },
  user_preferences: {
    automation_level: 'moderate',
    review_threshold: 70,
    materiality_threshold: null,
    auto_post_permissions: []
  }
};

const defaultAutoPostCategories = [
  'office_supplies', 'software_subscriptions', 'utilities', 'rent',
  'travel', 'meals', 'professional_services', 'insurance',
  'advertising', 'shipping', 'bank_fees', 'telecom'
];

function ListEditor({ value, onChange, placeholder, label }) {
  const [inputValue, setInputValue] = useState('');
  
  const addItem = () => {
    if (inputValue.trim() && !value.includes(inputValue.trim())) {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
    }
  };
  
  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>}
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
          placeholder={placeholder}
          className="rounded-sm flex-1"
        />
        <Button type="button" variant="outline" onClick={addItem} className="rounded-sm px-3">
          <Plus size={16} />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((item, i) => (
            <span key={`${item}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-zinc-100 border border-zinc-200 rounded-sm">
              {item}
              <button type="button" onClick={() => removeItem(i)} className="text-zinc-400 hover:text-zinc-600">
                <XIcon size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileForm({ profile, onSave, onCancel, saving }) {
  const [form, setForm] = useState(profile);

  const updateField = (section, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  // Initialize auto_post_permissions if empty
  useEffect(() => {
    if (!form.user_preferences.auto_post_permissions?.length) {
      setForm(prev => ({
        ...prev,
        user_preferences: {
          ...prev.user_preferences,
          auto_post_permissions: defaultAutoPostCategories.map(cat => ({
            category: cat,
            allowed: false
          }))
        }
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form onSubmit={handleSubmit}>
      <Tabs defaultValue="identity" className="space-y-6">
        <TabsList className="bg-zinc-100 p-1 rounded-sm grid grid-cols-4 w-full">
          <TabsTrigger value="identity" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="profile-tab-identity">
            <Buildings size={14} className="mr-1.5" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="accounting" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="profile-tab-accounting">
            <Scales size={14} className="mr-1.5" />
            Accounting
          </TabsTrigger>
          <TabsTrigger value="operational" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="profile-tab-operational">
            <GearSix size={14} className="mr-1.5" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="preferences" className="rounded-sm text-xs data-[state=active]:bg-white" data-testid="profile-tab-preferences">
            <Sliders size={14} className="mr-1.5" />
            Preferences
          </TabsTrigger>
        </TabsList>

        {/* COMPANY IDENTITY */}
        <TabsContent value="identity" className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6">
            <div>
              <h3 className="font-semibold mb-1">Company Identity</h3>
              <p className="text-sm text-zinc-500">Core business information for context-aware classification</p>
            </div>
            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Profile Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Acme Corp"
                  required
                  className="rounded-sm"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Company Name</Label>
                <Input
                  value={form.company_identity.company_name}
                  onChange={(e) => updateField('company_identity', 'company_name', e.target.value)}
                  placeholder="Legal entity name"
                  className="rounded-sm"
                  data-testid="company-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Industry</Label>
                <Select
                  value={form.company_identity.industry}
                  onValueChange={(v) => updateField('company_identity', 'industry', v)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="profile-industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology / Software</SelectItem>
                    <SelectItem value="law_firm">Law Firm</SelectItem>
                    <SelectItem value="accounting_firm">Accounting Firm</SelectItem>
                    <SelectItem value="retail">Retail / E-commerce</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="professional_services">Professional Services</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="hospitality">Hospitality / Food Service</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="nonprofit">Non-profit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Entity Structure</Label>
                <Select
                  value={form.company_identity.entity_structure}
                  onValueChange={(v) => updateField('company_identity', 'entity_structure', v)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="profile-entity-select">
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="llc">LLC</SelectItem>
                    <SelectItem value="s_corp">S Corporation</SelectItem>
                    <SelectItem value="c_corp">C Corporation</SelectItem>
                    <SelectItem value="nonprofit">Non-profit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Business Model</Label>
              <Textarea
                value={form.company_identity.business_model}
                onChange={(e) => updateField('company_identity', 'business_model', e.target.value)}
                placeholder="Describe the core business model (e.g., SaaS with annual contracts, B2B consulting, etc.)"
                className="rounded-sm resize-none"
                rows={3}
                data-testid="profile-business-model-input"
              />
            </div>

            <ListEditor
              value={form.company_identity.revenue_streams}
              onChange={(v) => updateField('company_identity', 'revenue_streams', v)}
              placeholder="Add revenue stream (e.g., Subscription, Consulting)"
              label="Revenue Streams"
            />
            <ListEditor
              value={form.company_identity.locations}
              onChange={(v) => updateField('company_identity', 'locations', v)}
              placeholder="Add location (e.g., New York, Remote)"
              label="Locations"
            />
            <ListEditor
              value={form.company_identity.departments}
              onChange={(v) => updateField('company_identity', 'departments', v)}
              placeholder="Add department/class (e.g., Sales, Engineering)"
              label="Departments / Classes"
            />
          </div>
        </TabsContent>

        {/* ACCOUNTING POLICY */}
        <TabsContent value="accounting" className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6">
            <div>
              <h3 className="font-semibold mb-1">Accounting Policy</h3>
              <p className="text-sm text-zinc-500">Hard constraints that override AI pattern recognition</p>
            </div>
            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Accounting Basis</Label>
                <Select
                  value={form.accounting_policy.accounting_basis}
                  onValueChange={(v) => updateField('accounting_policy', 'accounting_basis', v)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="profile-acct-basis-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash Basis</SelectItem>
                    <SelectItem value="accrual">Accrual Basis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Capitalization Threshold ($)</Label>
                <Input
                  type="number"
                  value={form.accounting_policy.capitalization_threshold || ''}
                  onChange={(e) => updateField('accounting_policy', 'capitalization_threshold', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="e.g., 2500"
                  className="rounded-sm font-mono"
                  data-testid="profile-cap-threshold-input"
                />
                <p className="text-xs text-zinc-400">Items above this are capitalized as fixed assets</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Prepaid Expense Policy</Label>
              <Textarea
                value={form.accounting_policy.prepaid_expense_policy}
                onChange={(e) => updateField('accounting_policy', 'prepaid_expense_policy', e.target.value)}
                placeholder="e.g., Prepay over $1,000 with benefit > 1 month is amortized..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-prepaid-policy-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Revenue Recognition Notes</Label>
              <Textarea
                value={form.accounting_policy.revenue_recognition_notes}
                onChange={(e) => updateField('accounting_policy', 'revenue_recognition_notes', e.target.value)}
                placeholder="e.g., SaaS revenue recognized ratably over subscription period..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-rev-rec-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Reimbursement Treatment</Label>
              <Textarea
                value={form.accounting_policy.reimbursement_treatment}
                onChange={(e) => updateField('accounting_policy', 'reimbursement_treatment', e.target.value)}
                placeholder="e.g., Employee reimbursements coded to original expense category..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-reimburse-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Loan Handling Rules</Label>
              <Textarea
                value={form.accounting_policy.loan_handling_rules}
                onChange={(e) => updateField('accounting_policy', 'loan_handling_rules', e.target.value)}
                placeholder="e.g., Principal payments to Notes Payable, interest to Interest Expense..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-loan-rules-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Owner Compensation Logic</Label>
              <Textarea
                value={form.accounting_policy.owner_compensation_logic}
                onChange={(e) => updateField('accounting_policy', 'owner_compensation_logic', e.target.value)}
                placeholder="e.g., Owner draws as distributions, not salary. Reasonable comp via payroll..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-owner-comp-input"
              />
            </div>
          </div>
        </TabsContent>

        {/* OPERATIONAL CONTEXT */}
        <TabsContent value="operational" className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6">
            <div>
              <h3 className="font-semibold mb-1">Operational Context</h3>
              <p className="text-sm text-zinc-500">Business operations that inform transaction interpretation</p>
            </div>
            <Separator />

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Seasonality</Label>
              <Textarea
                value={form.operational_context.seasonality}
                onChange={(e) => updateField('operational_context', 'seasonality', e.target.value)}
                placeholder="e.g., Q4 is peak revenue. Jan-Feb are slow months. Large inventory buys in Sep..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-seasonality-input"
              />
            </div>

            <ListEditor
              value={form.operational_context.top_vendors}
              onChange={(v) => updateField('operational_context', 'top_vendors', v)}
              placeholder="Add vendor (e.g., AWS, Dropbox, ADP)"
              label="Top Vendors"
            />

            <ListEditor
              value={form.operational_context.common_payment_rails}
              onChange={(v) => updateField('operational_context', 'common_payment_rails', v)}
              placeholder="Add payment method (e.g., ACH, Wire, Credit Card)"
              label="Common Payment Rails"
            />

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Monthly Close Process</Label>
              <Textarea
                value={form.operational_context.monthly_close_process}
                onChange={(e) => updateField('operational_context', 'monthly_close_process', e.target.value)}
                placeholder="e.g., Close by 10th, reconcile bank by 5th, review accruals by 7th..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-close-process-input"
              />
            </div>

            <ListEditor
              value={form.operational_context.large_known_contracts}
              onChange={(v) => updateField('operational_context', 'large_known_contracts', v)}
              placeholder="Add contract (e.g., AWS - $50k/yr, Office lease - $8k/mo)"
              label="Large Known Contracts"
            />

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Expected Expense Cycles</Label>
              <Textarea
                value={form.operational_context.expected_expense_cycles}
                onChange={(e) => updateField('operational_context', 'expected_expense_cycles', e.target.value)}
                placeholder="e.g., Insurance annual payment in March. Quarterly tax estimates..."
                className="rounded-sm resize-none"
                rows={2}
                data-testid="profile-expense-cycles-input"
              />
            </div>
          </div>
        </TabsContent>

        {/* USER PREFERENCES */}
        <TabsContent value="preferences" className="space-y-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6">
            <div>
              <h3 className="font-semibold mb-1">User Intent & Preferences</h3>
              <p className="text-sm text-zinc-500">Control how aggressively the AI acts on decisions</p>
            </div>
            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Automation Level</Label>
                <Select
                  value={form.user_preferences.automation_level}
                  onValueChange={(v) => updateField('user_preferences', 'automation_level', v)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="profile-automation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conservative">Conservative (review most)</SelectItem>
                    <SelectItem value="moderate">Moderate (balanced)</SelectItem>
                    <SelectItem value="aggressive">Aggressive (auto-post more)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Review Threshold (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.user_preferences.review_threshold || ''}
                  onChange={(e) => updateField('user_preferences', 'review_threshold', e.target.value ? parseInt(e.target.value) : 70)}
                  className="rounded-sm font-mono"
                  data-testid="profile-review-threshold-input"
                />
                <p className="text-xs text-zinc-400">Confidence below this triggers review</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Materiality Threshold ($)</Label>
                <Input
                  type="number"
                  value={form.user_preferences.materiality_threshold || ''}
                  onChange={(e) => updateField('user_preferences', 'materiality_threshold', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="e.g., 5000"
                  className="rounded-sm font-mono"
                  data-testid="profile-materiality-input"
                />
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="font-medium mb-1">Auto-Post Permissions by Category</h4>
              <p className="text-xs text-zinc-500 mb-4">Enable auto-posting for specific expense categories when confidence is high and no policy conflicts exist</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(form.user_preferences.auto_post_permissions || []).map((perm, i) => (
                  <div key={perm.category} className="flex items-center justify-between py-2 px-3 bg-zinc-50 border border-zinc-100 rounded-sm">
                    <span className="text-sm capitalize">{perm.category.replace(/_/g, ' ')}</span>
                    <Switch
                      checked={perm.allowed}
                      onCheckedChange={(checked) => {
                        const updated = [...form.user_preferences.auto_post_permissions];
                        updated[i] = { ...updated[i], allowed: checked };
                        setForm(prev => ({
                          ...prev,
                          user_preferences: { ...prev.user_preferences, auto_post_permissions: updated }
                        }));
                      }}
                      data-testid={`auto-post-${perm.category}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Save/Cancel */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-zinc-200">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="save-profile-button">
          <Check size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
    </form>
  );
}

export default function ClientProfilesPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [healthScores, setHealthScores] = useState({});
  const [clientHealth, setClientHealth] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const [clientRes, healthRes] = await Promise.all([getClients(), getClientsHealthSummary()]);
      setClients(clientRes.data.clients);
      const scores = {};
      (healthRes.data.clients || []).forEach(h => { scores[h.client_id] = h; });
      setHealthScores(scores);
      if (clientRes.data.clients.length > 0 && !selectedClient) {
        setSelectedClient(clientRes.data.clients[0]);
      }
    } catch (error) {
      toast.error('Failed to load client profiles');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (selectedClient) {
      getClientHealth(selectedClient.client_id).then(res => setClientHealth(res.data)).catch(() => {});
      getClientMembers(selectedClient.client_id).then(res => setMembers(res.data.members)).catch(() => {});
    }
  }, [selectedClient]);

  const handleInvite = async () => {
    if (!inviteEmail || !selectedClient) return;
    try {
      await inviteClientMember(selectedClient.client_id, { email: inviteEmail, role: inviteRole });
      toast.success('Member invited');
      setInviteEmail('');
      const res = await getClientMembers(selectedClient.client_id);
      setMembers(res.data.members);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedClient) return;
    try {
      await removeClientMember(selectedClient.client_id, memberId);
      toast.success('Member removed');
      const res = await getClientMembers(selectedClient.client_id);
      setMembers(res.data.members);
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleCreate = async (profileData) => {
    setSaving(true);
    try {
      const response = await createClient(profileData);
      toast.success('Client profile created');
      setIsCreating(false);
      setSelectedClient(response.data);
      fetchClients();
    } catch (error) {
      toast.error('Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (profileData) => {
    if (!editingClient) return;
    setSaving(true);
    try {
      const response = await updateClient(editingClient.client_id, profileData);
      toast.success('Client profile updated');
      setEditingClient(null);
      setSelectedClient(response.data);
      fetchClients();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client profile? This cannot be undone.')) return;
    try {
      await deleteClient(clientId);
      toast.success('Client profile deleted');
      if (selectedClient?.client_id === clientId) {
        setSelectedClient(null);
      }
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  if (loading) {
    return (
      <Layout title="Client Profiles">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Show create/edit form
  if (isCreating) {
    return (
      <Layout title="Create Client Profile">
        <ProfileForm
          profile={emptyProfile}
          onSave={handleCreate}
          onCancel={() => setIsCreating(false)}
          saving={saving}
        />
      </Layout>
    );
  }

  if (editingClient) {
    return (
      <Layout title={`Edit: ${editingClient.name}`}>
        <ProfileForm
          profile={editingClient}
          onSave={handleUpdate}
          onCancel={() => setEditingClient(null)}
          saving={saving}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Client Profiles">
      <div data-testid="client-profiles-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-600">
              Business context profiles are referenced on every AI decision. Each profile contains company identity, 
              accounting policies, operational context, and automation preferences.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/onboarding')} className="h-10 rounded-sm" data-testid="wizard-button">
              Guided Setup
            </Button>
            <Button onClick={() => setIsCreating(true)} className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="create-client-button">
              <Plus size={18} className="mr-2" />
              New Client
            </Button>
          </div>
        </div>

        {clients.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Buildings size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-2">No client profiles yet</p>
            <p className="text-sm text-zinc-500 mb-4">
              Create a business context profile for each client. The AI references these profiles on every transaction decision.
            </p>
            <Button onClick={() => setIsCreating(true)} className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm">
              <Plus size={18} className="mr-2" />
              Create First Profile
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client List */}
            <div className="col-span-1 space-y-3">
              {clients.map((client) => (
                <button
                  key={client.client_id}
                  onClick={() => setSelectedClient(client)}
                  data-testid={`client-card-${client.client_id}`}
                  className={`w-full text-left p-4 border rounded-sm transition-colors duration-150 ${
                    selectedClient?.client_id === client.client_id
                      ? 'bg-zinc-100 border-zinc-400'
                      : 'bg-white border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{client.name}</h3>
                      <p className="text-xs text-zinc-500 mt-1">
                        {client.company_identity?.industry?.replace(/_/g, ' ') || 'No industry set'}
                        {client.company_identity?.entity_structure && ` / ${client.company_identity.entity_structure.replace(/_/g, ' ')}`}
                      </p>
                    </div>
                    {healthScores[client.client_id] && (
                      <span className={`flex-shrink-0 ml-2 w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold ${
                        healthScores[client.client_id].grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                        healthScores[client.client_id].grade === 'B' ? 'bg-blue-100 text-blue-700' :
                        healthScores[client.client_id].grade === 'C' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`} data-testid={`health-badge-${client.client_id}`}>
                        {healthScores[client.client_id].grade}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Client Detail */}
            <div className="col-span-2">
              {selectedClient ? (
                <div className="bg-white border border-zinc-200 rounded-sm">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                      <h2 className="font-semibold text-lg">{selectedClient.name}</h2>
                      <p className="text-xs text-zinc-500">
                        {selectedClient.company_identity?.company_name || 'Company name not set'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingClient(selectedClient)}
                        className="rounded-sm"
                        data-testid="edit-client-button"
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(selectedClient.client_id)}
                        className="rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="delete-client-button"
                      >
                        <Trash size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-6 max-h-[calc(100vh-20rem)] overflow-auto">
                    {/* Company Identity */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Buildings size={18} weight="duotone" className="text-zinc-600" />
                        <h3 className="text-xs uppercase tracking-widest text-zinc-500">Company Identity</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-zinc-500">Industry:</span> <span className="font-medium capitalize">{selectedClient.company_identity?.industry?.replace(/_/g, ' ') || '—'}</span></div>
                        <div><span className="text-zinc-500">Entity:</span> <span className="font-medium capitalize">{selectedClient.company_identity?.entity_structure?.replace(/_/g, ' ') || '—'}</span></div>
                        <div className="col-span-2"><span className="text-zinc-500">Business Model:</span> <span className="font-medium">{selectedClient.company_identity?.business_model || '—'}</span></div>
                      </div>
                      {selectedClient.company_identity?.departments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {selectedClient.company_identity.departments.map((d) => (
                            <span key={d} className="px-2 py-1 text-xs bg-zinc-100 rounded-sm">{d}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Accounting Policy */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Scales size={18} weight="duotone" className="text-zinc-600" />
                        <h3 className="text-xs uppercase tracking-widest text-zinc-500">Accounting Policy</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-zinc-500">Basis:</span> <span className="font-medium capitalize">{selectedClient.accounting_policy?.accounting_basis || '—'}</span></div>
                        <div><span className="text-zinc-500">Cap Threshold:</span> <span className="font-mono font-medium">{selectedClient.accounting_policy?.capitalization_threshold ? `$${selectedClient.accounting_policy.capitalization_threshold.toLocaleString()}` : '—'}</span></div>
                      </div>
                      {selectedClient.accounting_policy?.loan_handling_rules && (
                        <p className="text-xs text-zinc-600 mt-2 bg-zinc-50 p-2 rounded-sm border border-zinc-100">
                          <strong>Loans:</strong> {selectedClient.accounting_policy.loan_handling_rules}
                        </p>
                      )}
                      {selectedClient.accounting_policy?.owner_compensation_logic && (
                        <p className="text-xs text-zinc-600 mt-2 bg-zinc-50 p-2 rounded-sm border border-zinc-100">
                          <strong>Owner Comp:</strong> {selectedClient.accounting_policy.owner_compensation_logic}
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Preferences */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Sliders size={18} weight="duotone" className="text-zinc-600" />
                        <h3 className="text-xs uppercase tracking-widest text-zinc-500">Preferences</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-zinc-500">Automation:</span>
                          <span className={`ml-1 font-medium capitalize ${
                            selectedClient.user_preferences?.automation_level === 'aggressive' ? 'text-emerald-600' :
                            selectedClient.user_preferences?.automation_level === 'conservative' ? 'text-amber-600' : 'text-zinc-700'
                          }`}>
                            {selectedClient.user_preferences?.automation_level || 'moderate'}
                          </span>
                        </div>
                        <div><span className="text-zinc-500">Review at:</span> <span className="font-mono font-medium">{selectedClient.user_preferences?.review_threshold || 70}%</span></div>
                        <div><span className="text-zinc-500">Materiality:</span> <span className="font-mono font-medium">{selectedClient.user_preferences?.materiality_threshold ? `$${selectedClient.user_preferences.materiality_threshold.toLocaleString()}` : '—'}</span></div>
                      </div>
                      
                      {selectedClient.user_preferences?.auto_post_permissions?.some(p => p.allowed) && (
                        <div className="mt-3">
                          <p className="text-xs text-zinc-500 mb-2">Auto-post enabled for:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedClient.user_preferences.auto_post_permissions.filter(p => p.allowed).map((p) => (
                              <span key={p.category} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm capitalize">
                                {p.category.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Health Score Breakdown */}
                    {clientHealth && clientHealth.client_id === selectedClient.client_id && (
                      <>
                        <Separator />
                        <div data-testid="health-breakdown">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs uppercase tracking-widest text-zinc-500">Client Health Score</h3>
                            <div className={`flex items-center gap-2 px-3 py-1 rounded-sm text-sm font-bold ${
                              clientHealth.grade === 'A' ? 'bg-emerald-100 text-emerald-700' :
                              clientHealth.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                              clientHealth.grade === 'C' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {clientHealth.grade} — {clientHealth.composite_score}/100
                            </div>
                          </div>
                          <div className="space-y-3">
                            {Object.entries(clientHealth.components || {}).map(([key, comp]) => (
                              <div key={key} className="flex items-center gap-3">
                                <div className="flex-1">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="capitalize text-zinc-600">{key.replace(/_/g, ' ')}</span>
                                    <span className="font-mono">{comp.score}/100 ({comp.weight}%)</span>
                                  </div>
                                  <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${
                                      comp.score >= 80 ? 'bg-emerald-500' :
                                      comp.score >= 60 ? 'bg-blue-500' :
                                      comp.score >= 40 ? 'bg-amber-500' : 'bg-red-500'
                                    }`} style={{ width: `${comp.score}%` }}></div>
                                  </div>
                                  <p className="text-xs text-zinc-400 mt-0.5">{comp.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          {clientHealth.recommendations?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-zinc-100">
                              <p className="text-xs text-zinc-500 mb-1">Recommendations:</p>
                              {clientHealth.recommendations.map((r, i) => (
                                <p key={`rec-${i}-${r.slice(0, 20)}`} className="text-xs text-zinc-600">- {r}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Team Members */}
                    <Separator />
                    <div data-testid="team-members">
                      <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Team Members</h3>
                      <div className="space-y-2 mb-3">
                        {members.map((m) => (
                          <div key={m.member_id || m.user_id} className="flex items-center justify-between py-2 px-3 bg-zinc-50 border border-zinc-100 rounded-sm">
                            <div>
                              <span className="text-sm font-medium">{m.name || m.email}</span>
                              {m.name && <span className="text-xs text-zinc-500 ml-2">{m.email}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs uppercase rounded-sm ${
                                m.role === 'owner' ? 'bg-zinc-200 text-zinc-700' :
                                m.role === 'admin' ? 'bg-blue-50 text-blue-700' :
                                m.role === 'editor' ? 'bg-emerald-50 text-emerald-700' :
                                'bg-zinc-100 text-zinc-600'
                              }`}>{m.role}</span>
                              {m.role !== 'owner' && m.member_id && (
                                <button onClick={() => handleRemoveMember(m.member_id)} className="p-1 text-red-400 hover:text-red-600">
                                  <Trash size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="Email to invite..."
                          className="rounded-sm flex-1 h-9 text-sm"
                          data-testid="invite-email-input"
                        />
                        <Select value={inviteRole} onValueChange={setInviteRole}>
                          <SelectTrigger className="w-28 rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={handleInvite} disabled={!inviteEmail} className="bg-zinc-950 text-white rounded-sm h-9 text-xs" data-testid="invite-button">
                          Invite
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center text-zinc-500">
                  <p>Select a client profile to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
